import { cn } from "@/lib/utils";
import { TriangleAlert } from "lucide-react";
import { Dialog as DialogPrimitive } from "radix-ui";
import type { ReactNode } from "react";

/** The product's modal shell — a centered Radix dialog styled like the command palette (same scrim,
 *  popover surface, enter-only animation) so every dialog reads as one system. Optional `icon` renders a
 *  leading badge beside the title (fills the header, sets a clear tone); `children` is the body/footer
 *  below. Portals to the body; Esc, overlay-click, and programmatic close all route through `onOpenChange`.
 *  Enter animation only (no exit) so a dialog whose confirm mutates/navigates can't leave a stuck scrim. */
export function Modal({
  open,
  onOpenChange,
  icon,
  title,
  description,
  children,
  className,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  icon?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  className?: string;
}) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/50 data-[state=open]:animate-in data-[state=open]:fade-in" />
        <DialogPrimitive.Content
          // Radix warns when nothing describes the dialog; opt out explicitly when there's no description.
          {...(description ? {} : { "aria-describedby": undefined })}
          className={cn(
            "fixed left-1/2 top-1/2 z-50 w-[400px] max-w-[92vw] -translate-x-1/2 -translate-y-1/2 rounded-xl border bg-popover p-5 text-popover-foreground shadow-2xl outline-none data-[state=open]:animate-in data-[state=open]:fade-in data-[state=open]:zoom-in-95 data-[state=open]:duration-150",
            className,
          )}
        >
          <div className={cn(icon && "flex gap-3.5")}>
            {icon ? <div className="shrink-0">{icon}</div> : null}
            <div className="min-w-0 flex-1">
              <DialogPrimitive.Title className="text-[15px] font-semibold leading-tight tracking-tight text-foreground">
                {title}
              </DialogPrimitive.Title>
              {description ? (
                <DialogPrimitive.Description className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
                  {description}
                </DialogPrimitive.Description>
              ) : null}
            </div>
          </div>
          {children}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

/** A confirmation gate in the product's modal style — a toned icon badge, the decision, and its
 *  consequence, then Cancel or a single commit action (destructive styling by default when `destructive`).
 *  Reusable for any "are you sure?" before an irreversible action. Confirm runs the action, then closes. */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  destructive = false,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  onConfirm: () => void;
}) {
  const tone = destructive ? "var(--color-destructive)" : "var(--color-warning)";
  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      icon={
        <div
          className="grid size-9 place-items-center rounded-full"
          style={{ backgroundColor: `color-mix(in oklab, ${tone} 12%, transparent)`, color: tone }}
        >
          <TriangleAlert className="size-[18px]" aria-hidden />
        </div>
      }
      title={title}
      description={description}
    >
      <div className="mt-5 flex justify-end gap-2">
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          className="inline-flex h-9 items-center rounded-md border bg-card px-3.5 text-sm font-medium text-foreground outline-none transition-colors hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring"
        >
          {cancelLabel}
        </button>
        <button
          type="button"
          onClick={() => {
            onConfirm();
            onOpenChange(false);
          }}
          className={cn(
            "inline-flex h-9 items-center rounded-md px-3.5 text-sm font-medium outline-none transition-opacity hover:opacity-90 focus-visible:ring-2 focus-visible:ring-ring",
            destructive ? "text-white" : "bg-foreground text-background",
          )}
          style={destructive ? { backgroundColor: "var(--color-destructive)" } : undefined}
        >
          {confirmLabel}
        </button>
      </div>
    </Modal>
  );
}
